import { capturePostHogEvent } from "./posthog";

/**
 * Trustbox funnel events — organized by stage of the AAARRR pirate funnel
 * (Awareness → Acquisition → Activation → Retention → Revenue → Referral)
 * plus four cross-cutting categories (choice, hesitation, friction, intent).
 *
 * Server-authoritative events (payment_completed, simulation_completed,
 * simulation_not_reached, voice_clone_failed) are also fired from the
 * backend (Stripe webhook + Twilio status callback + voice-clone processor)
 * for resilience against client-side abandon. The client-side fire stays as
 * a fast-path UX hook; server-side fire is the source of truth for funnels.
 */
export type FunnelEventType =
  // ───── AWARENESS / TOFU ─────
  | "landing_cta_clicked"
  | "pricing_section_viewed"
  | "faq_opened"
  | "testimonial_viewed"
  | "comparison_table_viewed"
  | "learn_article_viewed"

  // ───── ACQUISITION ─────
  | "signup_started"
  | "signup_method_selected"
  | "account_created"
  | "email_verified"
  | "login_started"
  | "login_completed"

  // ───── ACTIVATION — Onboarding ─────
  | "onboarding_started"
  | "onboarding_step_completed"
  | "onboarding_completed"
  | "onboarding_abandoned"

  // ───── ACTIVATION — Simulation wizard ─────
  | "simulate_wizard_started"
  | "simulate_wizard_step_completed"
  | "template_selected"
  | "voice_upload_started"
  | "voice_upload_completed"
  | "voice_upload_failed"
  | "voice_clone_started"
  | "voice_cloned"
  | "voice_clone_failed"
  | "call_initiated"
  | "simulation_launched"
  | "simulation_completed"
  | "simulation_not_reached"

  // ───── REVENUE — Checkout ─────
  | "checkout_started"
  | "checkout_initiated"
  | "checkout_redirected_stripe"
  | "payment_completed"
  | "payment_failed"
  | "checkout_abandoned"
  | "lead_form_submitted"
  | "lead_form_failed"

  // ───── ACTIVATION — Report ─────
  | "report_viewed"
  | "report_pdf_downloaded"
  | "certificate_downloaded"

  // ───── REFERRAL — Share ─────
  | "share_button_clicked"
  | "share_method_selected"
  | "report_shared"
  | "referral_link_copied"

  // ───── RETENTION ─────
  | "dashboard_viewed"
  | "repeat_simulation_started"

  // ───── CHOICE / COMPARISON (decision tracking — “why X over Y”) ─────
  | "option_compared"
  | "option_selected"

  // ───── HESITATION (proxy for doubt/friction) ─────
  | "field_focused_then_blurred_empty"
  | "cta_hover_no_click"
  | "step_revisited"
  | "time_on_step_reported"
  | "scroll_depth_reached"
  | "back_button_pressed"

  // ───── FRICTION (errors, retries, slow paths) ─────
  | "error_shown"
  | "retry_attempted"
  | "validation_failed"
  | "slow_response_observed"

  // ───── /try DEMO FUNNEL (anonymous, no signup) ─────
  // Top-of-funnel: visitor → hears clone → CTA to paid simulation.
  // try_clone_cta_clicked is the conversion event from demo to /dashboard/simulate.
  | "try_clone_page_viewed"
  | "try_clone_recording_started"
  | "try_clone_recording_completed"
  | "try_clone_audio_uploaded"
  | "try_clone_submitted"
  | "try_clone_succeeded"
  | "try_clone_failed"
  | "try_clone_cta_clicked"
  | "try_clone_reset"
  // ───── LANDING PAGE INTERACTIVE WIDGETS ─────
  | "phone_simulator_started"
  | "phone_simulator_completed"
  | "phone_simulator_declined"
  | "risk_quiz_started"
  | "risk_quiz_question_answered"
  | "risk_quiz_completed";

export type FunnelEventMetadata = Record<
  string,
  string | number | boolean | null | undefined
>;

/**
 * Fire-and-forget funnel event tracking via PostHog.
 * Never throws — tracking failures must not break the user flow.
 */
export function trackEvent(
  event: FunnelEventType,
  metadata?: FunnelEventMetadata,
): void {
  try {
    capturePostHogEvent(event, metadata);
  } catch {
    // Silent fail — tracking must never block user flow
  }
}
